/* eslint-disable @typescript-eslint/no-explicit-any */
// React and hooks
import React, { useState } from 'react'

// Third-party libraries
import moment from 'moment'
import { Col, Dropdown, Row, SplitButton } from 'react-bootstrap'

// Types and interfaces
import {
  AppointmentRequest,
  AppointmentResponse,
  IAppointment,
  IProvider,
} from '@dataTypes/appointment.type'
import { Provider } from '@dataTypes/provider.types'

// Relative imports
import { DragAndDropModalFooterProps } from '../DayCalendar/DayCalendarTemplate'

import Button from '@components/common/Button/Button.component'
import { AppDispatch } from '@store/index'
import { formatDateTime } from '@utils/time.util'
import { updateAppointment } from '@store/actions/appointment.action'

export const CustomDateTemplate = (
  props: any,
  apptList: AppointmentResponse[],
): React.ReactElement => {
  const date = moment(props.date).format('MM/DD/YYYY')
  const formattedDay = moment(props.date).format('ddd')
  const formattedDate = moment(props.date).format('DD')
  const isToday = moment(props.date).isSame(moment(), 'day')

  // Format both the event's start date and the target date to 'MM/DD/YYYY'
  const eventCount = apptList.filter(
    (event: AppointmentResponse) =>
      moment(event.appt_date).format('MM/DD/YYYY') === date &&
      !('IsBlock' in (event as any)),
  ).length

  return (
    <>
      <Row
        className={`week-header-row ${isToday ? 'week-header-today' : ''}`}
        style={{
          margin: 0,
          backgroundColor: isToday ? 'var(--blue-200)' : 'var(--blue-100)',
        }}
      >
        <Col
          md={6}
          className="custom-day-header"
          style={{ padding: 0, textAlign: 'left' }}
        >
          {formattedDay}{' '}
          <span className="custom-date-header">{formattedDate}</span>
        </Col>
        <Col
          md={6}
          className="custom-header-count-wrapper"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: 0,
          }}
        >
          <i className="cell-count-icon mgc_group_3_fill"></i>{' '}
          <span
            className={`custom-header-count ${formattedDate.replace(' ', '-')}`}
          >
            {eventCount}
          </span>
        </Col>
      </Row>
    </>
  )
}

const handleConfirm = (
  type: 'Switch Provider' | 'Move with provider',
  data: IAppointment,
  providerId: string | null,
  dispatch: AppDispatch,
  closeDeleteModal: () => void,
  providerList: Provider[],
): void => {
  if (!data) return

  let updatedAppt: Partial<AppointmentRequest> = {
    appt_start: formatDateTime(`${data.appointment_start_date}`, 'HH:mm'),
    appt_end: formatDateTime(`${data.appointment_end_date}`, 'HH:mm'),
    appt_date: formatDateTime(`${data.appointment_start_date}`, 'YYYY-MM-DD'),
    operatory_id: data?.operatory_id || '',
  }

  // Handle switching provider logic
  if (type === 'Switch Provider' && providerId) {
    const providerInfo = providerList.find(
      (doctor) => doctor.provider_id === providerId,
    )

    if (providerInfo) {
      updatedAppt = {
        ...updatedAppt,
        provider_id: providerInfo.provider_id,
      }
    }
  }

  dispatch(
    updateAppointment({
      appointment_id: data.appointment_id,
      updates: updatedAppt,
      updateByDragAndDrop: true,
    }),
  ).then(() => {
    setTimeout(() => {
      closeDeleteModal()
    }, 100)
  })
}

export const DragAndDropModalFooter: React.FC<DragAndDropModalFooterProps> = ({
  data,
  dispatch,
  closeDeleteModal,
  providerList,
}) => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [providerName, setProviderName] = useState<string | null>(null)

  // Handler for selecting a provider
  // Handler for selecting a provider
  const handleSelectProvider = (provider: IProvider): void => {
    setSelectedProvider(provider.provider_id)
    setProviderName(`${provider.first_name} ${provider.last_name}`)
  }

  // Handler for confirming actions
  const handleActionConfirm = (
    type: 'Switch Provider' | 'Move with provider',
  ): void => {
    handleConfirm(
      type,
      data,
      selectedProvider,
      dispatch,
      closeDeleteModal,
      providerList,
    )
  }

  return (
    <>
      <Button
        className="button-tertiary me-2"
        size="md"
        onClick={closeDeleteModal}
      >
        Cancel
      </Button>
      <SplitButton
        drop={'down'}
        variant="secondary"
        title={providerName || 'Switch Provider'}
        id="switch-provider"
        className="switch-provider"
        onClick={() => handleActionConfirm('Switch Provider')}
      >
        {providerList.map((provider) => (
          <Dropdown.Item
            key={provider.provider_id}
            onClick={() => handleSelectProvider(provider)}
          >
            {`${provider.first_name} ${provider.last_name}`}
          </Dropdown.Item>
        ))}
      </SplitButton>
      <Button
        className="button-primary"
        size="md"
        onClick={() => handleActionConfirm('Move with provider')}
      >
        Move with provider
      </Button>
    </>
  )
}

export const DragAndDropModalBody: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="delete-modal-body">
      <div>Are you sure you want to move this appointment?</div>
      {/* <span className="delete-modal-name">{`Room No. ${roomInfo?.name}`}</span> */}
    </div>
  )
}
